/*
 * Sock Bundle Builder B - "Buy More & Save More"
 *
 * Loaded from sections/main-product.liquid when the `sock_bundle_builder`
 * theme setting is B. Pairs with snippets/bundle-builder-b.liquid and
 * assets/bundle-builder-b.css
 *
 * Flow: pick a pack row -> it expands into one slot per pair -> slot 1 is
 * seeded with the product whose page this is, the rest are filled from the
 * chooser modal -> Add to Cart posts every slot to /cart/add.js in one go.
 *
 * Mirrors the custom-element + /cart/add.js + PUB_SUB_EVENTS.cartUpdate
 * pattern used in assets/product-form.js and assets/bundle-builder-au.js.
 */
if (!customElements.get('bundle-builder-b')) {
  customElements.define(
    'bundle-builder-b',
    class BundleBuilderB extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        try {
          this.init();
          this.initialized = true;
        } catch (err) {
          console.error('[bundle-builder-b] init failed', err);
        }
      }

      disconnectedCallback() {
        // The chooser lives on <body>, so it has to be cleaned up by hand when
        // the section is re-rendered (theme editor, section reload).
        this.closeModal();
        [this._modal, this._sizeGuideModal].forEach((modal) => {
          if (modal && modal.isConnected) modal.remove();
        });
        this._modal = null;
        this._sizeGuideModal = null;
      }

      /* ---------- Init ---------- */

      init() {
        const dataScript = this.querySelector('script[data-bbb-data]');
        if (!dataScript) throw new Error('missing data script');
        this.data = JSON.parse(dataScript.textContent);

        this.products = Array.isArray(this.data.products) ? this.data.products : [];
        this.productsById = new Map(this.products.map((p) => [p.id, p]));
        this.currentProduct =
          this.productsById.get(this.data.currentProductId) || this.products[0] || null;

        this.tierEls = Array.from(this.querySelectorAll('[data-bbb-tier]'));
        this.headEls = Array.from(this.querySelectorAll('[data-bbb-tier-head]'));
        this.atcBtn = this.querySelector('[data-bbb-atc]');
        this.atcLabel = this.querySelector('[data-bbb-atc-label]');
        this.errorEl = this.querySelector('[data-bbb-error]');

        // State: no pack selected until the shopper picks one.
        this.tier = 0;
        this.slots = [];

        this.headEls.forEach((head) => {
          head.addEventListener('click', () => {
            this.selectTier(parseInt(head.dataset.bbbTierHead, 10));
          });
        });

        // Keyboard: arrow keys move between pack rows, like a real radiogroup.
        const group = this.querySelector('.bbb-tiers');
        if (group) {
          group.addEventListener('keydown', (e) => this.onGroupKeydown(e));
        }

        this.atcBtn.addEventListener('click', () => this.onAddToCart());

        // Size guide opens in a lightweight in-page modal rather than a tab.
        this.querySelectorAll('.bbb-size-guide').forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            this.openSizeGuideModal(link.href);
          });
        });

        this.cart =
          document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (this.cart) this.atcBtn.setAttribute('aria-haspopup', 'dialog');

        this.hideLegacyBlocks();
        this.setupDynamicCheckoutIntercept();

        this.renderTierPrices();
        this.renderAtc();
      }

      /* ---------- Legacy block hiding (same approach as builder A) ---------- */

      hideLegacyBlocks() {
        const parent = this.parentElement;
        if (!parent) return;

        [
          '.sock-style-selector',
          '.sock-style-selected-name',
          '.variants-size-box',
          '.pack-selector',
          'variant-radios',
          'variant-selects',
          'quantity-input',
        ].forEach((sel) => {
          parent.querySelectorAll(sel).forEach((el) => {
            el.style.setProperty('display', 'none', 'important');
          });
        });

        // Keep the theme's <product-form> intact (Shopify's dynamic checkout
        // script owns the form reference) but hide its own Add to Cart, and
        // relocate the whole form below our ATC so the Shop Pay / Afterpay /
        // PayPal buttons still render where the design expects them.
        try {
          const productForm =
            parent.querySelector('product-form.product-form') ||
            parent.querySelector('.product-form') ||
            document.querySelector('.product__info-container product-form.product-form');
          if (productForm) {
            productForm
              .querySelectorAll('button[name="add"], .product-form__error-message-wrapper')
              .forEach((el) => el.style.setProperty('display', 'none', 'important'));

            // The theme ships `input[name="id"]` disabled and lets its variant
            // picker enable it. We hide that picker, so seed the input here or
            // the dynamic checkout buttons render with zero height.
            const variantIdInput = productForm.querySelector('input[name="id"]');
            if (variantIdInput) {
              variantIdInput.removeAttribute('disabled');
              const seed = this.firstAvailableVariant(this.currentProduct);
              if (seed) {
                variantIdInput.value = String(seed.id);
                variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            const wrap =
              parent.querySelector('.bbb-dynamic-checkout') || document.createElement('div');
            wrap.className = 'bbb-dynamic-checkout';
            if (!wrap.isConnected) {
              const hint = this.querySelector('.bbb-atc-hint');
              if (hint) hint.insertAdjacentElement('afterend', wrap);
              else this.atcBtn.insertAdjacentElement('afterend', wrap);
            }
            wrap.appendChild(productForm);
          }
        } catch (err) {
          console.warn('[bundle-builder-b] could not relocate product form', err);
        }

        parent.querySelectorAll('.buy--buttons').forEach((el) => {
          if (el.querySelector('product-form, .shopify-payment-button')) return;
          el.style.setProperty('display', 'none', 'important');
        });

        // The bare "<b>Select Style:</b>" label has no class; find it via its
        // sibling and hide it too.
        Array.from(parent.children).forEach((child) => {
          if (
            child.tagName === 'B' &&
            child.nextElementSibling &&
            child.nextElementSibling.classList &&
            child.nextElementSibling.classList.contains('sock-style-selected-name')
          ) {
            child.style.setProperty('display', 'none', 'important');
          }
        });

        const prev = this.previousElementSibling;
        if (prev && prev.tagName === 'HR') {
          prev.style.setProperty('display', 'none', 'important');
        }
      }

      /* ---------- Variant helpers ---------- */

      firstAvailableVariant(product) {
        if (!product || !Array.isArray(product.variants)) return null;
        return product.variants.find((v) => v.available) || null;
      }

      findVariant(product, size) {
        if (!product || !Array.isArray(product.variants)) return null;
        return product.variants.find((v) => v.size === size) || null;
      }

      // Prefer keeping the shopper's current size when swapping styles, then
      // fall back to the first size this style actually has in stock.
      resolveVariant(product, preferredSize) {
        const preferred = preferredSize ? this.findVariant(product, preferredSize) : null;
        if (preferred && preferred.available) return preferred;
        return this.firstAvailableVariant(product);
      }

      static formatSize(size) {
        return String(size || '').replace(/\s*\/\s*/g, '/');
      }

      // Only Shopify's own `description` text is fit to show a shopper.
      // Anything else (a TypeError, a JSON parse failure on an HTML error
      // page) gets replaced with wording that means something to them.
      static shopperMessage(err) {
        const fallback = 'Sorry, we could not add your bundle just now. Please try again.';
        const msg = err && err.message;
        if (!msg) return fallback;
        if (/token|JSON|undefined|null|fetch|NetworkError|Load failed/i.test(msg)) return fallback;
        return msg;
      }

      /* ---------- Tier selection ---------- */

      selectTier(qty) {
        if (!qty) return;
        const isSame = qty === this.tier;
        this.tier = qty;

        if (!isSame) {
          // Keep whatever is already chosen, trimmed to the new capacity, and
          // always seed slot 1 with the product whose page this is.
          const kept = this.slots.slice(0, qty);
          if (!kept[0] && this.currentProduct) {
            const v = this.firstAvailableVariant(this.currentProduct);
            if (v) {
              kept[0] = {
                productId: this.currentProduct.id,
                variantId: v.id,
                size: v.size,
                title: this.currentProduct.title,
                thumb: this.currentProduct.thumb,
                price: v.price,
              };
            }
          }
          this.slots = kept;
        }

        this.clearError();
        this.renderTiers();
        this.renderAtc();
      }

      onGroupKeydown(e) {
        const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
        if (!keys.includes(e.key)) return;
        const head = e.target.closest('[data-bbb-tier-head]');
        if (!head) return;
        e.preventDefault();
        const idx = this.headEls.indexOf(head);
        const step = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
        const next = this.headEls[(idx + step + this.headEls.length) % this.headEls.length];
        if (next) {
          next.focus();
          this.selectTier(parseInt(next.dataset.bbbTierHead, 10));
        }
      }

      /* ---------- Rendering ---------- */

      unitPriceCents(basePriceCents, discountPct) {
        // Shopify's automatic percentage discounts truncate the per-line
        // discount to whole cents, so mirror that here - otherwise the
        // advertised total is a cent or two under what the cart charges.
        const base = Number(basePriceCents) || 0;
        const pct = Number(discountPct) || 0;
        return base - Math.floor((base * pct) / 100);
      }

      renderTierPrices() {
        const base = Number(this.data.basePriceCents) || 0;
        const compareUnit = Number(this.data.compareAtCents) || base;

        this.headEls.forEach((head) => {
          const qty = parseInt(head.dataset.bbbTierHead, 10);
          const pct = parseFloat(head.dataset.bbbDiscount) || 0;
          const total = this.unitPriceCents(base, pct) * qty;
          const compare = compareUnit * qty;

          const totalEl = head.querySelector('[data-bbb-total]');
          const compareEl = head.querySelector('[data-bbb-compare]');
          const saveEl = head.querySelector('[data-bbb-save]');

          if (totalEl) totalEl.textContent = this.formatMoney(total);
          if (compareEl) {
            if (compare > total) {
              compareEl.textContent = this.formatMoney(compare);
              compareEl.hidden = false;
            } else {
              compareEl.textContent = '';
              compareEl.hidden = true;
            }
          }
          if (saveEl) {
            const save = compare - total;
            if (save > 0) {
              saveEl.textContent = 'SAVE ' + this.formatMoney(save);
              saveEl.hidden = false;
            } else {
              saveEl.hidden = true;
            }
          }
        });
      }

      renderTiers() {
        this.tierEls.forEach((tierEl) => {
          const qty = parseInt(tierEl.dataset.bbbTier, 10);
          const isSelected = qty === this.tier;
          const head = tierEl.querySelector('[data-bbb-tier-head]');
          const panel = tierEl.querySelector('[data-bbb-panel]');

          tierEl.classList.toggle('is-selected', isSelected);
          if (head) {
            head.setAttribute('aria-checked', isSelected ? 'true' : 'false');
            head.setAttribute('aria-expanded', isSelected ? 'true' : 'false');
            // Only the selected row (or the first, when nothing is selected)
            // is in the tab order, per radiogroup conventions.
            const focusable = isSelected || (this.tier === 0 && qty === 1);
            head.tabIndex = focusable ? 0 : -1;
          }
          if (panel) {
            panel.hidden = !isSelected;
            if (isSelected) this.renderSlots(panel, qty);
            else panel.querySelector('[data-bbb-slots]').innerHTML = '';
          }
        });
      }

      renderSlots(panel, qty) {
        const list = panel.querySelector('[data-bbb-slots]');
        if (!list) return;
        const frag = document.createDocumentFragment();

        for (let i = 0; i < qty; i++) {
          const slot = this.slots[i];
          const li = document.createElement('li');
          li.className = 'bbb-slot ' + (slot ? 'is-filled' : 'is-empty');

          if (slot) {
            const product = this.productsById.get(slot.productId);

            const thumbBtn = document.createElement('button');
            thumbBtn.type = 'button';
            thumbBtn.className = 'bbb-slot__thumb-btn';
            thumbBtn.setAttribute('aria-label', 'Change pair ' + (i + 1) + ', ' + slot.title);
            const img = document.createElement('img');
            img.className = 'bbb-slot__thumb';
            img.src = slot.thumb || '';
            img.alt = slot.title || '';
            img.loading = 'lazy';
            img.width = 48;
            img.height = 48;
            thumbBtn.appendChild(img);
            thumbBtn.addEventListener('click', () => this.openChooser(i));
            li.appendChild(thumbBtn);

            const body = document.createElement('div');
            body.className = 'bbb-slot__body';

            const titleBtn = document.createElement('button');
            titleBtn.type = 'button';
            titleBtn.className = 'bbb-slot__title';
            titleBtn.textContent = slot.title;
            titleBtn.title = 'Change this pair';
            titleBtn.addEventListener('click', () => this.openChooser(i));
            body.appendChild(titleBtn);

            const select = this.buildSizeSelect(product, slot.size);
            select.setAttribute('aria-label', 'Size for pair ' + (i + 1) + ', ' + slot.title);
            select.addEventListener('change', () => {
              const variant = this.findVariant(product, select.value);
              if (!variant) return;
              this.slots[i] = Object.assign({}, this.slots[i], {
                variantId: variant.id,
                size: variant.size,
                price: variant.price,
              });
              this.renderAtc();
            });
            body.appendChild(select);

            li.appendChild(body);
          } else {
            const add = document.createElement('button');
            add.type = 'button';
            add.className = 'bbb-slot__add';
            add.textContent = '+';
            add.setAttribute('aria-label', 'Choose pair ' + (i + 1));
            add.addEventListener('click', () => this.openChooser(i));
            li.appendChild(add);

            const choose = document.createElement('button');
            choose.type = 'button';
            choose.className = 'bbb-choose';
            choose.textContent = 'Choose';
            choose.setAttribute('aria-label', 'Choose pair ' + (i + 1));
            choose.addEventListener('click', () => this.openChooser(i));
            li.appendChild(choose);
          }

          frag.appendChild(li);
        }

        list.innerHTML = '';
        list.appendChild(frag);
      }

      buildSizeSelect(product, selectedSize) {
        const select = document.createElement('select');
        select.className = 'bbb-select';
        const variants = (product && product.variants) || [];
        variants.forEach((v) => {
          const opt = document.createElement('option');
          opt.value = v.size;
          opt.textContent = v.available
            ? BundleBuilderB.formatSize(v.size)
            : BundleBuilderB.formatSize(v.size) + ' - Sold out';
          opt.disabled = !v.available;
          if (v.size === selectedSize) opt.selected = true;
          select.appendChild(opt);
        });
        return select;
      }

      isComplete() {
        return this.tier > 0 && this.slots.filter(Boolean).length === this.tier;
      }

      renderAtc() {
        const disabled = !this.isComplete();
        this.atcBtn.toggleAttribute('disabled', disabled);
        this.atcBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        if (this.atcLabel) this.atcLabel.textContent = 'Add to Cart';
      }

      /* ---------- Style chooser modal ---------- */

      openChooser(slotIndex) {
        this._slotIndex = slotIndex;
        const modal = this.ensureModal();
        // Default each row's size to the size already chosen for this slot, so
        // swapping a style keeps the fit the shopper picked.
        const slot = this.slots[slotIndex];
        const preferred = slot ? slot.size : (this.slots[0] && this.slots[0].size) || '';
        modal.querySelectorAll('[data-bbb-row]').forEach((row) => {
          const select = row.querySelector('select');
          if (!select) return;
          const match = Array.from(select.options).find(
            (o) => o.value === preferred && !o.disabled
          );
          if (match) select.value = preferred;
          this.syncRowAvailability(row);
        });
        this.openModal(modal);
      }

      ensureModal() {
        if (this._modal && this._modal.isConnected) return this._modal;

        const modal = document.createElement('div');
        modal.className = 'bbb-modal';
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML =
          '<div class="bbb-modal__backdrop" data-bbb-close></div>' +
          '<div class="bbb-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="BbbModalTitle">' +
          '<div class="bbb-modal__head">' +
          '<h2 class="bbb-modal__title" id="BbbModalTitle">Bundle Builder &ndash; Scroll Down To See All Socks.</h2>' +
          '<button type="button" class="bbb-modal__close" data-bbb-close aria-label="Close style chooser">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
          '</div>' +
          '<ul class="bbb-modal__list" role="list"></ul>' +
          '<div class="bbb-modal__foot">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
          '<span>Your bundle discount will be applied at checkout</span>' +
          '</div>' +
          '</div>';

        const list = modal.querySelector('.bbb-modal__list');
        this.products.forEach((p) => list.appendChild(this.buildModalRow(p)));

        modal.addEventListener('click', (e) => {
          if (e.target.closest('[data-bbb-close]')) this.closeModal();
        });
        modal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeModal();
          } else if (e.key === 'Tab') {
            this.trapFocus(e);
          }
        });

        document.body.appendChild(modal);
        this._modal = modal;
        return modal;
      }

      buildModalRow(product) {
        const li = document.createElement('li');
        li.className = 'bbb-modal__row';
        li.dataset.bbbRow = String(product.id);

        const img = document.createElement('img');
        img.className = 'bbb-modal__thumb';
        img.src = product.thumb || '';
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 96;
        img.height = 96;
        li.appendChild(img);

        const info = document.createElement('div');
        info.className = 'bbb-modal__info';

        const name = document.createElement('p');
        name.className = 'bbb-modal__name';
        name.textContent = product.title;
        info.appendChild(name);

        const prices = document.createElement('div');
        prices.className = 'bbb-modal__prices';
        const price = document.createElement('span');
        price.className = 'bbb-modal__price';
        price.textContent = this.formatMoney(product.price);
        prices.appendChild(price);
        if (product.compareAt > product.price) {
          const compare = document.createElement('s');
          compare.className = 'bbb-modal__compare';
          compare.textContent = this.formatMoney(product.compareAt);
          prices.appendChild(compare);
        }
        info.appendChild(prices);

        const label = document.createElement('label');
        label.className = 'bbb-modal__size-label';
        label.textContent = 'Size';
        const selectId = 'BbbSize-' + product.id;
        label.setAttribute('for', selectId);
        info.appendChild(label);

        const select = this.buildSizeSelect(product, null);
        select.className = 'bbb-modal__select';
        select.id = selectId;
        const firstAvailable = this.firstAvailableVariant(product);
        if (firstAvailable) select.value = firstAvailable.size;
        select.addEventListener('change', () => this.syncRowAvailability(li));
        info.appendChild(select);

        li.appendChild(info);

        const choose = document.createElement('button');
        choose.type = 'button';
        choose.className = 'bbb-modal__choose';
        choose.textContent = 'Choose';
        choose.setAttribute('aria-label', 'Choose ' + product.title);
        choose.addEventListener('click', () => this.chooseProduct(product, select.value));
        li.appendChild(choose);

        return li;
      }

      syncRowAvailability(row) {
        const productId = parseInt(row.dataset.bbbRow, 10);
        const product = this.productsById.get(productId);
        const select = row.querySelector('select');
        const choose = row.querySelector('.bbb-modal__choose');
        if (!product || !select || !choose) return;
        const variant = this.findVariant(product, select.value);
        const ok = !!(variant && variant.available);
        row.classList.toggle('is-oos', !ok);
        choose.disabled = !ok;
        choose.textContent = ok ? 'Choose' : 'Sold out';
      }

      chooseProduct(product, size) {
        const variant = this.resolveVariant(product, size);
        if (!variant) return;
        const index = this._slotIndex == null ? this.slots.length : this._slotIndex;
        this.slots[index] = {
          productId: product.id,
          variantId: variant.id,
          size: variant.size,
          title: product.title,
          thumb: product.thumb,
          price: variant.price,
        };
        this.closeModal();
        this.clearError();
        this.renderTiers();
        this.renderAtc();
      }

      /* ---------- Modal open/close plumbing ---------- */

      openModal(modal) {
        this._lastFocus = document.activeElement;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        this.lockScroll();
        const close = modal.querySelector('.bbb-modal__close');
        if (close) close.focus();
        modal.querySelector('.bbb-modal__list').scrollTop = 0;
      }

      closeModal() {
        [this._modal, this._sizeGuideModal].forEach((modal) => {
          if (!modal) return;
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
        });
        this.unlockScroll();
        if (this._lastFocus && typeof this._lastFocus.focus === 'function') {
          this._lastFocus.focus();
          this._lastFocus = null;
        }
      }

      // iOS Safari ignores `overflow: hidden` on <body> for touch scrolling, so
      // pin the page and restore the offset on close.
      lockScroll() {
        if (document.body.classList.contains('bbb-modal-open')) return;
        this._scrollY = window.scrollY || window.pageYOffset || 0;
        const body = document.body;
        this._prevBodyStyle = {
          position: body.style.position,
          top: body.style.top,
          left: body.style.left,
          right: body.style.right,
          width: body.style.width,
        };
        body.style.position = 'fixed';
        body.style.top = -this._scrollY + 'px';
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.classList.add('bbb-modal-open');
      }

      unlockScroll() {
        const body = document.body;
        if (!body.classList.contains('bbb-modal-open')) return;
        const prev = this._prevBodyStyle || {};
        body.style.position = prev.position || '';
        body.style.top = prev.top || '';
        body.style.left = prev.left || '';
        body.style.right = prev.right || '';
        body.style.width = prev.width || '';
        body.classList.remove('bbb-modal-open');
        window.scrollTo(0, this._scrollY || 0);
      }

      trapFocus(e) {
        const dialog = e.currentTarget.querySelector('.bbb-modal__dialog');
        if (!dialog) return;
        const focusables = Array.from(
          dialog.querySelectorAll('button:not([disabled]), select, a[href], [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      /* ---------- Add to Cart ---------- */

      async onAddToCart() {
        if (!this.isComplete()) {
          this.showError('Choose your bundle before adding to cart.');
          return;
        }

        this.clearError();
        this.atcBtn.classList.add('is-loading');

        const body = { items: this.slots.filter(Boolean).map((s) => ({ id: s.variantId, quantity: 1 })) };
        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          // Shopify answers with an HTML error page when it throttles or
          // errors, so a bare res.json() would surface "Unexpected token '<'"
          // to the shopper. Fall back to our own wording instead.
          const json = await res.json().catch(() => null);
          if (!res.ok || !json || (json.status && json.description)) {
            throw new Error(
              (json && json.description) ||
                'Sorry, we could not add your bundle just now. Please try again.'
            );
          }

          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-builder-b',
              cartData: json,
            });
          }

          const drawer = this.cart || document.querySelector('cart-drawer');
          if (drawer && drawer.tagName === 'CART-DRAWER') {
            if (json.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(json);
            } else if (typeof drawer.open === 'function') {
              try {
                const [drawerHtml, iconHtml] = await Promise.all([
                  fetch('/?section_id=cart-drawer').then((r) => r.text()),
                  fetch('/?section_id=cart-icon-bubble').then((r) => r.text()),
                ]);
                drawer.renderContents({
                  sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': iconHtml },
                });
              } catch (_) {
                drawer.open();
              }
            }
          }

          this.resetBundle();
        } catch (err) {
          console.error('[bundle-builder-b] add to cart failed', err);
          this.showError(BundleBuilderB.shopperMessage(err));
        } finally {
          this.atcBtn.classList.remove('is-loading');
        }
      }

      resetBundle() {
        this.tier = 0;
        this.slots = [];
        this.renderTiers();
        this.renderAtc();
      }

      /* ---------- Dynamic checkout (Shop Pay / Afterpay / PayPal) ---------- */

      setupDynamicCheckoutIntercept() {
        const wrap = this.querySelector('.bbb-dynamic-checkout');
        if (!wrap) return;
        this._dcBusy = false;

        window.addEventListener('pageshow', () => {
          this._dcBusy = false;
          clearTimeout(this._dcBusyTimer);
        });

        // These wallet buttons check out the form's single variant and skip
        // the cart entirely, which would drop every other pair. So take the
        // flow over: add the whole bundle, then go to /checkout, where the
        // same accelerated methods are still offered.
        const handler = async (e) => {
          const inPaymentBtn = e.target.closest('.shopify-payment-button');
          if (!inPaymentBtn) return;
          if (this._dcBusy) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();

          if (!this.isComplete()) {
            this.showError('Choose your bundle before checking out.');
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }

          this._dcBusy = true;
          clearTimeout(this._dcBusyTimer);
          this._dcBusyTimer = setTimeout(() => {
            this._dcBusy = false;
          }, 8000);

          this.clearError();

          try {
            const items = this.slots.filter(Boolean).map((s) => ({ id: s.variantId, quantity: 1 }));
            const res = await fetch(
              (window.routes && window.routes.cart_add_url) || '/cart/add.js',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items }),
              }
            );
            const json = await res.json().catch(() => null);
            if (!res.ok || !json || (json.status && json.description)) {
              throw new Error(
                (json && json.description) ||
                  'Sorry, we could not add your bundle just now. Please try again.'
              );
            }
            window.location.href = '/checkout';
          } catch (err) {
            console.error('[bundle-builder-b] dynamic checkout add failed', err);
            this._dcBusy = false;
            clearTimeout(this._dcBusyTimer);
            this.showError(BundleBuilderB.shopperMessage(err));
          }
        };

        wrap.addEventListener('click', handler, true);
        wrap.addEventListener(
          'pointerdown',
          (e) => {
            if (!e.target.closest('.shopify-payment-button')) return;
            e.stopPropagation();
          },
          true
        );
      }

      /* ---------- Size guide modal ---------- */

      openSizeGuideModal(href) {
        if (!this._sizeGuideModal) {
          const modal = document.createElement('div');
          modal.className = 'bbb-modal bbb-modal--guide';
          modal.setAttribute('aria-hidden', 'true');
          modal.innerHTML =
            '<div class="bbb-modal__backdrop" data-bbb-close></div>' +
            '<div class="bbb-modal__dialog" role="dialog" aria-modal="true" aria-label="Size Guide">' +
            '<div class="bbb-modal__head">' +
            '<h2 class="bbb-modal__title">Size Guide</h2>' +
            '<button type="button" class="bbb-modal__close" data-bbb-close aria-label="Close size guide">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
            '</button>' +
            '</div>' +
            '<div class="bbb-modal__list" style="padding:16px 24px"><p>Loading size guide&hellip;</p></div>' +
            '</div>';
          document.body.appendChild(modal);
          this._sizeGuideModal = modal;

          modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-bbb-close]')) this.closeModal();
          });
          modal.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              this.closeModal();
            } else if (e.key === 'Tab') {
              this.trapFocus(e);
            }
          });

          const bodyEl = modal.querySelector('.bbb-modal__list');
          fetch(href, { headers: { Accept: 'text/html' } })
            .then((r) => r.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content =
                doc.querySelector('.page__content') ||
                doc.querySelector('main .rte') ||
                doc.querySelector('main .page') ||
                doc.querySelector('#MainContent') ||
                doc.querySelector('main');
              if (content) {
                bodyEl.innerHTML = '';
                bodyEl.appendChild(content);
              } else {
                bodyEl.innerHTML =
                  '<p style="text-align:center">Unable to load size guide. ' +
                  '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
              }
            })
            .catch(() => {
              bodyEl.innerHTML =
                '<p style="text-align:center">Unable to load size guide. ' +
                '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
            });
        }

        this.openModal(this._sizeGuideModal);
      }

      /* ---------- Utilities ---------- */

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      formatMoney(cents) {
        const fmt = this.data.moneyFormat || '${{amount}}';
        const dollars = (cents / 100).toFixed(2);
        const withoutDecimals = Math.round(cents / 100).toString();
        return fmt
          .replace(/\{\{\s*amount\s*\}\}/g, dollars)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withoutDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withoutDecimals);
      }
    }
  );
}
